import Link from '@liveauctioneers/caterwaul-components/lib/Link/Link';
import { FormattedMessage } from '@liveauctioneers/hammer-ui-core/intl';
import React from 'react';
import styled from 'styled-components';

import { ShippingMethods } from '@/enums/shippingMethod';
import { shippingProviders } from '@/enums/shippingProviders';
import { useAppSelector } from '@/redux/hooks';
import { getCatalogAddress } from '@/redux/modules/catalog/catalogs/catalog.selectors';
import { ShippingQuote } from '@/types/ShippingQuote';

import ShippingMethodLabel from './ShippingMethodLabel';

type Props = {
    catalogId: number;
    numberOfItems: number;
    onReviewStep: boolean;
    sellerName: string;
    shippingMethod: ShippingMethods;
    shippingMethodValid: boolean;
    shippingQuote: ShippingQuote;
};

const ShippingMethodInactive = ({
    catalogId,
    numberOfItems,
    onReviewStep,
    sellerName,
    shippingMethod,
    shippingQuote,
}: Props) => {
    const { address1, city, postalCode, state } = useAppSelector((state) => getCatalogAddress(state, catalogId));

    const catalogAddress = `${address1}, ${city}, ${state}, ${postalCode}`;
    const catalogStreetAddress = `${address1}`;
    const catalogStateAddress = `, ${city}, ${state}, ${postalCode}`;

    const determineInactiveTitleText = () => {
        switch (shippingMethod) {
            case ShippingMethods.Pickup:
                return 'checkout.shippingMethod.method.localPickup';
            case ShippingMethods.BidderShipping:
                return 'checkout.shippingMethod.method.self';
            case ShippingMethods.HouseShipping:
                return 'checkout.shippingMethod.method.H';
            case ShippingMethods.LiveShipping:
                return 'checkout.shippingMethod.method.A';
            default:
                return 'checkout.shippingMethod.method.localPickup';
        }
    };

    const determineInactiveTooltipMessage = () => {
        switch (shippingMethod) {
            case ShippingMethods.Pickup:
                return (
                    <FormattedMessage
                        id="checkout.shippingMethod.popovers.localPickup"
                        values={{ location: catalogAddress }}
                    />
                );
            case ShippingMethods.BidderShipping:
                return <FormattedMessage id="checkout.shippingMethod.popovers.selfShip" />;
            case ShippingMethods.HouseShipping:
                return (
                    <FormattedMessage
                        id="checkout.shippingMethod.popovers.houseShip"
                        values={{ houseName: sellerName }}
                    />
                );
            case ShippingMethods.LiveShipping:
                return <FormattedMessage id="checkout.shippingMethod.popovers.laShip" />;
            default:
                return (
                    <FormattedMessage
                        id="checkout.shippingMethod.popovers.localPickup"
                        values={{ location: catalogAddress }}
                    />
                );
        }
    };

    const determineDetailMessage = () => {
        switch (shippingMethod) {
            case ShippingMethods.Pickup:
                return (
                    <FormattedMessage
                        id="checkout.shippingMethod.method.pickupDescription"
                        values={{
                            locationState: catalogStateAddress,
                            locationStreet: (
                                <Link
                                    key="checkout-shipping-method-pickup-street-link"
                                    rel="noopener"
                                    target="_blank"
                                    to={`https://maps.google.com/maps?q=${catalogAddress}`}
                                >
                                    {catalogStreetAddress}
                                </Link>
                            ),
                        }}
                    />
                );
            case ShippingMethods.HouseShipping:
                return (
                    <FormattedMessage
                        id="checkout.shippingMethod.method.inHouseDescription"
                        values={{ houseName: sellerName }}
                    />
                );
            case ShippingMethods.BidderShipping:
                return <FormattedMessage id="checkout.shippingMethod.method.selfDescription" />;
            case ShippingMethods.LiveShipping:
                return (
                    <FormattedMessage
                        id={
                            shippingQuote.transitDays
                                ? 'checkout.shippingMethod.method.laShippingDetails'
                                : 'checkout.shippingMethod.method.laShippingDetailsNoTransitDays'
                        }
                        values={{
                            days: shippingQuote.transitDays || 1,
                            numberOfItems,
                            shippingProvider: shippingProviders[shippingQuote.shippingProvider],
                        }}
                    />
                );
            default:
                return null;
        }
    };

    return (
        <StyledNonActiveDisplay>
            <ShippingMethodLabel
                darkGrey={!onReviewStep}
                info={determineDetailMessage()}
                popoverContent={determineInactiveTooltipMessage()}
                title={<FormattedMessage id={determineInactiveTitleText()} />}
            />
        </StyledNonActiveDisplay>
    );
};

export default ShippingMethodInactive;

const StyledNonActiveDisplay = styled.div`
    width: 100%;
`;
